/**
 * Newsletter Page - STORY-050
 *
 * Campaign creation and editing page with:
 * - WYSIWYG editor for content
 * - Segment selection
 * - Scheduling options
 * - Live preview
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { CampaignEditor } from '@/components/admin/marketing'
import { Spinner } from '@/components/ui/Spinner'
import { Card } from '@/components/ui/Card'
import {
  useCampaign,
  useCreateCampaign,
  useUpdateCampaign,
  useScheduleCampaign,
  useSendCampaign,
} from '@/hooks/useMarketing'
import type { CampaignFormData } from '@/types/marketing'
import { AlertCircle } from 'lucide-react'

export default function NewsletterPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditing = !!id && id !== 'new'

  // Queries
  const { data: campaign, isLoading, error } = useCampaign(isEditing ? id : '')

  // Mutations
  const createMutation = useCreateCampaign()
  const updateMutation = useUpdateCampaign()
  const scheduleMutation = useScheduleCampaign()
  const sendMutation = useSendCampaign()

  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async (data: CampaignFormData) => {
    setIsSaving(true)
    try {
      if (isEditing) {
        await updateMutation.mutateAsync({ id, data })
      } else {
        const newCampaign = await createMutation.mutateAsync(data)
        navigate(`/marketing/newsletter/${newCampaign.id}`, { replace: true })
      }
    } catch (error) {
      console.error('Failed to save campaign:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSchedule = async (data: CampaignFormData, scheduledAt: string) => {
    setIsSaving(true)
    try {
      // First save the campaign
      let campaignId = id
      if (isEditing) {
        await updateMutation.mutateAsync({ id, data })
      } else {
        const newCampaign = await createMutation.mutateAsync(data)
        campaignId = newCampaign.id
      }

      // Then schedule it
      if (campaignId) {
        await scheduleMutation.mutateAsync({ id: campaignId, scheduledAt })
        navigate('/marketing')
      }
    } catch (error) {
      console.error('Failed to schedule campaign:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSend = async (data: CampaignFormData) => {
    setIsSaving(true)
    try {
      // First save the campaign
      let campaignId = id
      if (isEditing) {
        await updateMutation.mutateAsync({ id, data })
      } else {
        const newCampaign = await createMutation.mutateAsync(data)
        campaignId = newCampaign.id
      }

      // Then send it
      if (campaignId) {
        await sendMutation.mutateAsync(campaignId)
        navigate('/marketing')
      }
    } catch (error) {
      console.error('Failed to send campaign:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleBack = () => {
    navigate('/marketing')
  }

  if (isEditing && isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner size="lg" />
      </div>
    )
  }

  if (isEditing && error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-brown-900 mb-2">
            Erreur de chargement
          </h2>
          <p className="text-brown-600 mb-4">
            Impossible de charger cette campagne.
          </p>
          <button
            onClick={handleBack}
            className="text-mint-600 hover:text-mint-700 font-medium"
          >
            Retour a la liste
          </button>
        </Card>
      </div>
    )
  }

  return (
    <div className="h-[calc(100vh-4rem)] -m-6">
      <CampaignEditor
        campaign={isEditing ? campaign : null}
        onSave={handleSave}
        onSchedule={handleSchedule}
        onSend={handleSend}
        onBack={handleBack}
        isLoading={isSaving}
      />
    </div>
  )
}
